import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Image as ImageIcon, Activity, Trash2, Download, Languages, Sparkles, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { CATEGORIES, CATEGORY_LABELS } from "@/components/media/types";

interface Stats {
  total: number;
  unused: number;
  withMultilingual: number;
  bySource: Record<string, number>;
  topUsed: { id: string; file_name: string; file_url: string; usage_count: number }[];
  apiUsageToday: { provider: string; count: number; cache_hits: number }[];
  cacheHitRatio: number;
}

export default function MediaAnalyticsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkQueries, setBulkQueries] = useState("");
  const [bulkCategory, setBulkCategory] = useState("uncategorized");
  const [bulkPerQuery, setBulkPerQuery] = useState(3);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [expansionCfg, setExpansionCfg] = useState<any>({
    enabled: false, frequency_hours: 24, queries_per_run: 10,
    images_per_query: 3, per_category_cap: 50,
  });
  const [savingCfg, setSavingCfg] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [gaps, setGaps] = useState<any>(null);
  const [analyzingGaps, setAnalyzingGaps] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const loadGaps = async () => {
    setAnalyzingGaps(true);
    try {
      const { data, error } = await supabase.functions.invoke("media-gap-analysis", { body: {} });
      if (error) throw error;
      setGaps(data);
    } catch (e: any) {
      toast({ title: "Could not analyze gaps", description: e.message, variant: "destructive" });
    } finally {
      setAnalyzingGaps(false);
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const { data, error } = await supabase.functions.invoke("backfill-article-images", {
        body: { limit: 50, fallback_external: true },
      });
      if (error) throw error;
      toast({
        title: "Backfill complete",
        description: `Assigned ${data.assigned}, no match ${data.skipped}, failed ${data.failed}`,
      });
      loadGaps();
      loadStats();
    } catch (e: any) {
      toast({ title: "Backfill failed", description: e.message, variant: "destructive" });
    } finally {
      setBackfilling(false);
    }
  };

  const loadStats = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [{ data: assets }, { data: usage }] = await Promise.all([
        supabase.from("media_assets")
          .select("id, file_name, file_url, source, usage_count, tags_en")
          .eq("user_id", user.id),
        supabase.from("api_usage_log")
          .select("provider, cache_hit")
          .eq("user_id", user.id)
          .gte("day", new Date().toISOString().slice(0, 10)),
      ]);

      const total = assets?.length || 0;
      const unused = (assets || []).filter((a) => !a.usage_count).length;
      const withMultilingual = (assets || []).filter((a) => (a.tags_en?.length || 0) > 0).length;
      const bySource: Record<string, number> = {};
      (assets || []).forEach((a) => {
        const k = a.source || "upload";
        bySource[k] = (bySource[k] || 0) + 1;
      });
      const topUsed = (assets || [])
        .filter((a) => (a.usage_count || 0) > 0)
        .sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0))
        .slice(0, 8) as Stats["topUsed"];

      const usageMap: Record<string, { count: number; cache_hits: number }> = {};
      (usage || []).forEach((u) => {
        const k = u.provider;
        if (!usageMap[k]) usageMap[k] = { count: 0, cache_hits: 0 };
        usageMap[k].count++;
        if (u.cache_hit) usageMap[k].cache_hits++;
      });
      const apiUsageToday = Object.entries(usageMap).map(([provider, v]) => ({ provider, ...v }));
      const totalCalls = (usage || []).length;
      const cacheHits = (usage || []).filter((u) => u.cache_hit).length;
      const cacheHitRatio = totalCalls ? cacheHits / totalCalls : 0;

      setStats({ total, unused, withMultilingual, bySource, topUsed, apiUsageToday, cacheHitRatio });
    } finally {
      setLoading(false);
    }
  };

  // Refresh all three panels when the auth user changes. The loaders are
  // defined below this useEffect and intentionally read fresh state via
  // closure each call — listing them as deps would force re-renders on
  // every save (loadStats is recreated on each render). The user-id key
  // is the right invalidation signal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadStats(); void loadExpansionCfg(); void loadGaps(); }, [user]);

  const loadExpansionCfg = async () => {
    if (!user) return;
    const { data } = await supabase.from("media_expansion_config")
      .select("*").eq("user_id", user.id).maybeSingle();
    if (data) setExpansionCfg(data);
  };

  const saveExpansionCfg = async (patch: Partial<typeof expansionCfg>) => {
    if (!user) return;
    setSavingCfg(true);
    const next = { ...expansionCfg, ...patch };
    setExpansionCfg(next);
    try {
      const { error } = await supabase.from("media_expansion_config").upsert({
        user_id: user.id,
        enabled: next.enabled,
        frequency_hours: next.frequency_hours,
        queries_per_run: next.queries_per_run,
        images_per_query: next.images_per_query,
        per_category_cap: next.per_category_cap,
      }, { onConflict: "user_id" });
      if (error) throw error;
    } catch (e: any) {
      toast({ title: "Could not save automation rule", description: e.message, variant: "destructive" });
    } finally {
      setSavingCfg(false);
    }
  };

  const handleExpandNow = async () => {
    setExpanding(true);
    try {
      const { data, error } = await supabase.functions.invoke("auto-expand-media", { body: {} });
      if (error) throw error;
      toast({
        title: "Media library expanded",
        description: `Imported ${data.imported}, skipped ${data.skipped}, failed ${data.failed} across ${data.queries_run} queries`,
      });
      loadStats(); loadExpansionCfg();
    } catch (e: any) {
      toast({ title: "Expansion failed", description: e.message, variant: "destructive" });
    } finally {
      setExpanding(false);
    }
  };

  const handleBulkImport = async () => {
    const queries = bulkQueries.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!queries.length) {
      toast({ title: "Add at least one keyword or category", variant: "destructive" });
      return;
    }
    setBulkRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("bulk-import-images", {
        body: { queries, category: bulkCategory, count_per_query: bulkPerQuery },
      });
      if (error) throw error;
      toast({
        title: "Bulk import complete",
        description: `Imported ${data.imported}, skipped ${data.skipped}, failed ${data.failed}`,
      });
      setBulkOpen(false);
      setBulkQueries("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Bulk import failed", description: e.message, variant: "destructive" });
    } finally {
      setBulkRunning(false);
    }
  };

  const handleNormalize = async () => {
    setNormalizing(true);
    try {
      const { data, error } = await supabase.functions.invoke("normalize-media-tags", {
        body: { only_missing: true, limit: 500 },
      });
      if (error) throw error;
      toast({ title: "Multilingual tags applied", description: `Updated ${data.updated} assets` });
      loadStats();
    } catch (e: any) {
      toast({ title: "Normalization failed", description: e.message, variant: "destructive" });
    } finally {
      setNormalizing(false);
    }
  };

  const handleCleanupUnused = async () => {
    if (!user) return;
    if (!confirm("Delete assets that have never been used and are older than 30 days? This cannot be undone.")) return;
    setCleaningUp(true);
    try {
      const cutoff = new Date(Date.now() - 30 * 86400_000).toISOString();
      const { data: targets } = await supabase
        .from("media_assets")
        .select("id, file_path")
        .eq("user_id", user.id)
        .eq("usage_count", 0)
        .lt("created_at", cutoff)
        .limit(500);
      if (!targets?.length) {
        toast({ title: "Nothing to clean up" });
        return;
      }
      // Best-effort storage removal then DB delete
      await supabase.storage.from("media").remove(targets.map((t) => t.file_path));
      await supabase.from("media_assets").delete().in("id", targets.map((t) => t.id));
      toast({ title: "Cleanup complete", description: `Removed ${targets.length} unused assets` });
      loadStats();
    } catch (e: any) {
      toast({ title: "Cleanup failed", description: e.message, variant: "destructive" });
    } finally {
      setCleaningUp(false);
    }
  };

  const handleAttributionExport = async () => {
    if (!user) return;
    const { data } = await supabase.from("media_assets")
      .select("file_name, source, attribution, source_url, usage_count")
      .eq("user_id", user.id)
      .not("attribution", "is", null);
    const csv = [
      "file_name,source,attribution,source_url,usage_count",
      ...(data || []).map((r) => [r.file_name, r.source, r.attribution, r.source_url, r.usage_count]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `media-attributions-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Media Analytics & Operations</h1>
          <p className="text-sm text-muted-foreground">Usage, costs, and bulk maintenance for your media library</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleAttributionExport}>
            <Download className="h-4 w-4 mr-1" />Export attributions
          </Button>
          <Button variant="outline" size="sm" onClick={handleNormalize} disabled={normalizing}>
            {normalizing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Languages className="h-4 w-4 mr-1" />}
            Apply multilingual tags
          </Button>
          <Button variant="outline" size="sm" onClick={handleCleanupUnused} disabled={cleaningUp} className="text-destructive">
            {cleaningUp ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Clean up unused (30d+)
          </Button>
        </div>
      </div>

      {/* Auto-expansion automation */}
      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Auto-expand media library
            </span>
            <div className="flex items-center gap-3">
              <Button size="sm" variant="outline" onClick={handleExpandNow} disabled={expanding}>
                {expanding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                Expand now
              </Button>
              <div className="flex items-center gap-2">
                <Label htmlFor="auto-exp" className="text-xs">Automatic</Label>
                <Switch id="auto-exp" checked={!!expansionCfg.enabled}
                  onCheckedChange={(v) => saveExpansionCfg({ enabled: v })} disabled={savingCfg} />
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Continuously discovers and imports new images using your article topics, categories, and trending keywords.
            Skips duplicates and respects per-category limits.
          </p>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Run every (hours)</Label>
              <Input type="number" min={1} max={168} value={expansionCfg.frequency_hours}
                onChange={(e) => saveExpansionCfg({ frequency_hours: Math.max(1, +e.target.value || 24) })}
                className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Queries per run</Label>
              <Input type="number" min={1} max={50} value={expansionCfg.queries_per_run}
                onChange={(e) => saveExpansionCfg({ queries_per_run: Math.max(1, +e.target.value || 10) })}
                className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Images per query</Label>
              <Input type="number" min={1} max={10} value={expansionCfg.images_per_query}
                onChange={(e) => saveExpansionCfg({ images_per_query: Math.max(1, +e.target.value || 3) })}
                className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">Per-category cap</Label>
              <Input type="number" min={10} max={1000} value={expansionCfg.per_category_cap}
                onChange={(e) => saveExpansionCfg({ per_category_cap: Math.max(10, +e.target.value || 50) })}
                className="h-8 text-xs" />
            </div>
          </div>
          {expansionCfg.last_run_at && (
            <div className="text-[11px] text-muted-foreground">
              Last run: {new Date(expansionCfg.last_run_at).toLocaleString()} —{" "}
              imported {expansionCfg.last_run_summary?.imported ?? 0},
              skipped {expansionCfg.last_run_summary?.skipped ?? 0},
              failed {expansionCfg.last_run_summary?.failed ?? 0}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Smart gap detection */}
      <Card className="border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-amber-600" />
              Coverage gaps
              {gaps?.summary && (
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  {gaps.summary.coverage_pct}% of articles have images
                </span>
              )}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadGaps} disabled={analyzingGaps}>
                {analyzingGaps ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Activity className="h-4 w-4 mr-1" />}
                Re-analyze
              </Button>
              <Button size="sm" onClick={handleBackfill} disabled={backfilling || !gaps?.summary?.articles_missing_image}>
                {backfilling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-1" />}
                Backfill missing images
                {gaps?.summary?.articles_missing_image > 0 && ` (${gaps.summary.articles_missing_image})`}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!gaps && !analyzingGaps && (
            <p className="text-xs text-muted-foreground">Click "Re-analyze" to scan article coverage.</p>
          )}
          {gaps && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">
                  Categories needing more images
                </div>
                <div className="space-y-1">
                  {(gaps.coverage_gaps || []).slice(0, 6).map((g: any) => (
                    <div key={g.category} className="flex justify-between text-xs py-1 border-b border-border/40">
                      <span className="capitalize">{g.category.replace(/-/g, " ")}</span>
                      <span className="text-muted-foreground">
                        {g.asset_count} imgs · {g.article_count} articles
                        {g.missing_articles > 0 && (
                          <span className="text-amber-600 ml-1">({g.missing_articles} no img)</span>
                        )}
                      </span>
                    </div>
                  ))}
                  {!gaps.coverage_gaps?.length && (
                    <p className="text-xs text-muted-foreground">All categories well covered.</p>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold mb-1.5 text-muted-foreground uppercase tracking-wide">
                  Hot tags missing in library
                </div>
                <div className="flex flex-wrap gap-1">
                  {(gaps.hot_tags_missing || []).slice(0, 12).map((t: any) => (
                    <span key={t.tag}
                      className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/30">
                      {t.tag} <span className="opacity-60">×{t.frequency}</span>
                    </span>
                  ))}
                  {!gaps.hot_tags_missing?.length && (
                    <p className="text-xs text-muted-foreground">No critical tag gaps.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Total assets</div>
          <div className="text-2xl font-bold">{stats?.total ?? 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Unused</div>
          <div className="text-2xl font-bold">{stats?.unused ?? 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Multilingual-tagged</div>
          <div className="text-2xl font-bold">{stats?.withMultilingual ?? 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground">Cache hit ratio (today)</div>
          <div className="text-2xl font-bold">{Math.round((stats?.cacheHitRatio ?? 0) * 100)}%</div>
        </CardContent></Card>
      </div>

      {/* By source + API usage */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ImageIcon className="h-4 w-4" />By source</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(stats?.bySource || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="capitalize">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
            {!Object.keys(stats?.bySource || {}).length && <p className="text-xs text-muted-foreground">No assets yet</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" />API usage today</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {stats?.apiUsageToday.map((u) => (
              <div key={u.provider} className="flex justify-between text-sm">
                <span className="capitalize">{u.provider}</span>
                <span className="font-medium">
                  {u.count} call{u.count !== 1 ? "s" : ""}
                  {u.cache_hits > 0 && <span className="text-muted-foreground ml-1">({u.cache_hits} cached)</span>}
                </span>
              </div>
            ))}
            {!stats?.apiUsageToday.length && <p className="text-xs text-muted-foreground">No API calls today</p>}
          </CardContent>
        </Card>
      </div>

      {/* Top used */}
      <Card>
        <CardHeader><CardTitle className="text-base">Most-used images</CardTitle></CardHeader>
        <CardContent>
          {!stats?.topUsed.length && <p className="text-xs text-muted-foreground">No usage yet</p>}
          <div className="grid grid-cols-8 gap-2">
            {stats?.topUsed.map((a) => (
              <div key={a.id} className="relative">
                <img src={a.file_url} alt={a.file_name} loading="lazy" className="w-full h-20 object-cover rounded" />
                <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 rounded">×{a.usage_count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bulk import */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Bulk import from free stock</span>
            {!bulkOpen && <Button size="sm" onClick={() => setBulkOpen(true)}>Start bulk import</Button>}
          </CardTitle>
        </CardHeader>
        {bulkOpen && (
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Keywords or categories (one per line, or comma-separated)</Label>
              <Textarea
                value={bulkQueries} onChange={(e) => setBulkQueries(e.target.value)}
                placeholder="artificial intelligence&#10;cyber security&#10;stock market"
                rows={4} className="text-xs font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Destination category</Label>
                <Select value={bulkCategory} onValueChange={setBulkCategory}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="text-xs">{CATEGORY_LABELS[c] || c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Images per keyword (1-10)</Label>
                <Input type="number" min={1} max={10} value={bulkPerQuery}
                  onChange={(e) => setBulkPerQuery(Math.max(1, Math.min(10, +e.target.value || 1)))}
                  className="h-8 text-xs" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleBulkImport} disabled={bulkRunning}>
                {bulkRunning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Importing…</> : "Import now"}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Uses cached search results when available. Daily quota: 200 external searches per user.
            </p>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
